
import { useState, useEffect, useRef } from 'react';
import { useToast } from '@/hooks/use-toast';

interface VideoStreamOptions {
  isVideoActive: boolean;
  isMicActive: boolean;
}

export const useVideoStreamSetup = ({ isVideoActive }: { isVideoActive: boolean }) => {
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [cameraStreamReady, setCameraStreamReady] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const { toast } = useToast();
  const attemptRef = useRef(0);
  const lastVideoStateRef = useRef(isVideoActive);
  const lastCameraReleaseTimeRef = useRef(0);

  // This function ensures camera is fully released
  const forceReleaseCamera = async () => {
    console.log("Forcing camera release...");
    // Release any existing stream first
    if (stream) {
      stream.getTracks().forEach(track => {
        console.log(`Stopping track: ${track.kind}`);
        track.stop();
      });
      setStream(null);
    }
    
    // Approach 1: Get a temporary stream and immediately stop it
    try {
      const tempStream = await navigator.mediaDevices.getUserMedia({ 
        video: true, 
        audio: false 
      });
      tempStream.getTracks().forEach(track => {
        console.log(`Stopping temp camera track: ${track.kind}`);
        track.stop();
      });
    } catch (err) {
      console.log("Could not access camera to force release:", err);
    }
    
    // Approach 2: Set srcObject to null on video element
    if (videoRef.current) {
      videoRef.current.srcObject = null;
      console.log("Cleared video element source");
    }
    
    // Mark the time we released the camera
    lastCameraReleaseTimeRef.current = Date.now();
    setCameraStreamReady(false);
  };

  useEffect(() => {
    console.log("Video active state in hook:", isVideoActive);
    let currentStream: MediaStream | null = null;
    
    const setupCamera = async () => {
      try {
        // If we're turning off the camera
        if (!isVideoActive && lastVideoStateRef.current !== isVideoActive) {
          // Force camera release and cleanup
          await forceReleaseCamera();
          lastVideoStateRef.current = isVideoActive;
          return;
        }
        
        // If we're turning on the camera
        if (isVideoActive) {
          console.log("Setting up camera...");
          attemptRef.current += 1;
          
          // Ensure any existing streams are stopped first
          await forceReleaseCamera();
          
          // Wait a short delay to ensure resources are released
          // This helps on some browsers that need time to fully release the camera
          const timeSinceLastRelease = Date.now() - lastCameraReleaseTimeRef.current;
          if (timeSinceLastRelease < 300) {
            await new Promise(resolve => setTimeout(resolve, 300 - timeSinceLastRelease));
          }
          
          try {
            // Get new media stream with video only (audio is handled separately)
            currentStream = await navigator.mediaDevices.getUserMedia({ 
              video: {
                width: { ideal: 1280 },
                height: { ideal: 720 },
                facingMode: "user"
              },
              audio: false 
            });
            
            console.log("Camera stream obtained:", currentStream.id);
            setStream(currentStream);
            setCameraStreamReady(true);
            
            // Set the stream to the video element
            if (videoRef.current) {
              videoRef.current.srcObject = currentStream;
              console.log("Set new stream to video element");
              
              try {
                // Use a play promise to properly detect and handle errors
                const playPromise = videoRef.current.play();
                if (playPromise !== undefined) {
                  playPromise.then(() => {
                    console.log("Video playback started successfully");
                  }).catch(playErr => {
                    console.error("Error playing video:", playErr);
                    toast({
                      title: "Video playback error",
                      description: "There was an issue with your camera. Please check browser permissions and try again.",
                      variant: "destructive"
                    });
                  });
                }
              } catch (playErr) {
                console.error("Error in play attempt:", playErr);
              }
            }
          } catch (err) {
            console.error("Error accessing camera:", err);
            setCameraStreamReady(false);
            
            if (attemptRef.current <= 1) {
              toast({
                title: "Camera access error",
                description: "Could not access your camera. Please check your browser permissions and ensure no other app is using it.",
                variant: "destructive"
              });
            }
          }
        }
        
        // Update last video state
        lastVideoStateRef.current = isVideoActive;
      } catch (err) {
        console.error("Error in camera setup:", err);
        setCameraStreamReady(false);
      }
    };
    
    setupCamera();
    
    // Cleanup function to stop all tracks
    return () => {
      console.log("Cleanup function executing in hook...");
      forceReleaseCamera();
    };
  }, [isVideoActive, toast]);

  return {
    stream,
    cameraStreamReady,
    videoRef
  };
};
